import { inject, observer } from 'mobx-react'
import styled from '@emotion/styled'
import { Form, Field } from 'react-final-form'
import arrayMutators from 'final-form-arrays'
import { FieldArray } from 'react-final-form-arrays'
import { FORM_ERROR } from 'final-form'
import { withRouterProps } from '../../../utils'
import { LargeCheckbox } from '../../../guided-toolbox/checkbox'
import { Layout } from '../elements'
import CompanyStockButtons from './CompanyStockButtons'

const StyledSelectionTitle = styled(Layout.SelectionTitle)`
  color: #ce0b24;
`

const StyledSelectionBox = styled(Layout.SelectionBox)`
  margin: 28px 24px;
  border-color: #ce0b24;
`
const StyledSelectionField = styled(Layout.SelectionField)`
  display: flex;

  label {
    display: inline-flex;
    width: 36px;
    margin: 0;

    /* enlarge checkbox */
    transform: scale(1.5, 1.5) translate(5px, 5px);
  }
`
const StyledCheckboxLabel = styled(Layout.DisplaySmall)`
  display: inline;
  width: 96%;
  margin-left: 1%;
  color: #4a606a;
  cursor: pointer;
  align-items: center;
  strong {
    color: #ce0b24;
    display: inline;
  }
`

function CompanyStockConfirmation(props) {
  function initialValues() {
    // map across companyStocksSellAll to determine how many fields to display
    return {
      companyStocksSellAll: (props.companyStocksSellAll || []).map(
        ({ positionId, securityName }) => ({
          positionId,
          securityName,
          confirmed: false,
        })
      ),
    }
  }

  function validate(value) {
    // make sure they've confirmed sell-off of company stock(s) being sold to 100%
    return value ? undefined : 'Please confirm your company stock decision before continuing.'
  }

  function onSubmit(values) {
    // push them to the next page once valid
    return handleSubmit(values)
  }

  async function handleSubmit() {
    try {
      await Promise.resolve()

      const {
        navigate,
        offboarding: { nextRoute },
      } = props

      navigate(`/implement/${nextRoute('company-stock')}`)
    } catch (err) {
      console.error(err)
      return { [FORM_ERROR]: 'Oops! Something went wrong, please try again later' }
    }
  }

  const { config } = props.store

  return (
    <Form
      initialValues={initialValues()}
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      subscription={{ touched: true, errors: true, submitting: true, submitError: true }}
      render={({ touched, errors, submitting, submitError, handleSubmit }) => {
        const submittingError = !submitting && submitError
        const termsError =
          !submitting &&
          touched.companyStocksSellAll &&
          errors.companyStocksSellAll &&
          'Please confirm your company stock decision before continuing.'

        return (
          <form onSubmit={handleSubmit}>
            <FieldArray name='companyStocksSellAll'>
              {({ fields }) => {
                const fieldValues = Array.isArray(fields.value) ? fields.value : []

                if (fieldValues.length === 0) {
                  return null
                }

                return (
                  <StyledSelectionBox>
                    <StyledSelectionTitle>Confirm company stock will be sold</StyledSelectionTitle>

                    {fields.map((name, index) => {
                      const stock = fieldValues[index] || {}

                      return (
                        <Field
                          key={stock.positionId || name}
                          name={`${name}.confirmed`}
                          validate={validate}
                          render={({ input }) => (
                            <StyledSelectionField>
                              <LargeCheckbox
                                checked={input.value === true}
                                onChange={
                                  submitting
                                    ? () => {}
                                    : () => {
                                        input.onFocus()
                                        input.onChange(!input.value)
                                        input.onBlur()
                                      }
                                }
                              />
                              <StyledCheckboxLabel
                                onClick={
                                  submitting
                                    ? () => {}
                                    : () => {
                                        input.onFocus()
                                        input.onChange(!input.value)
                                        input.onBlur()
                                      }
                                }>
                                Please confirm you are aware that <strong>100%</strong> of your{' '}
                                {stock.securityName} <strong>will be sold</strong> if you elect to
                                have GuidedChoice implement the changes to your{' '}
                                {config.template.sponsorName} retirement plan account.
                              </StyledCheckboxLabel>
                            </StyledSelectionField>
                          )}
                        />
                      )
                    })}
                  </StyledSelectionBox>
                )
              }}
            </FieldArray>

            <Layout.ErrorMessage>{submittingError || termsError}</Layout.ErrorMessage>

            <CompanyStockButtons
              isDisabled={submitting || !!errors.terms}
              handleNext={handleSubmit}
            />
          </form>
        )
      }}
    />
  )
}

export default withRouterProps(inject('store', 'offboarding')(observer(CompanyStockConfirmation)))
